import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useApiClient } from "@/shared/api/ApiClientProvider";
import { useBootstrap } from "@/features/bootstrap/BootstrapProvider";
import type { GoogleLoginInput, SignInInput, SignUpInput } from "@/shared/api/contracts";
import type { AppSession } from "@/shared/types/app";

interface AuthContextValue {
  session: AppSession | null;
  loading: boolean;
  signIn: (input: SignInInput) => Promise<void>;
  // Resolves to hasCompletedOnboarding so the caller can route the user.
  signInWithGoogle: (input: GoogleLoginInput) => Promise<boolean>;
  signUp: (input: SignUpInput) => Promise<void>;
  refreshSession: () => Promise<AppSession | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const apiClient = useApiClient();
  const bootstrap = useBootstrap();
  const [session, setSession] = useState<AppSession | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [mutating, setMutating] = useState(false);

  useEffect(() => {
    const handleUnauthorized = () => {
      setSession(null);
      setHydrated(true);
    };
    window.addEventListener("liminl:unauthorized", handleUnauthorized);
    return () => window.removeEventListener("liminl:unauthorized", handleUnauthorized);
  }, []);

  useEffect(() => {
    if (bootstrap.status === "error") {
      // Clear only when bootstrap has no session (cold start fail or 401).
      // Transient mid-session refresh failures stay status "ready" in
      // BootstrapProvider, so they never hit this path and cannot log users out
      // after a successful Launch/complete.
      setSession(bootstrap.data?.session ?? null);
      setHydrated(true);
      return;
    }

    if (bootstrap.status !== "ready") return;
    setSession((current) => {
      const next = bootstrap.data?.session ?? null;
      // Don't clobber a fresher verified session with a stale bootstrap snapshot
      // (e.g. markOnboardingComplete rewrites bootstrap.data without re-fetching /me).
      if (
        current?.user.id &&
        next?.user.id === current.user.id &&
        current.user.emailVerified === true &&
        next.user.emailVerified === false
      ) {
        return {
          ...next,
          user: { ...next.user, emailVerified: true },
        };
      }
      return next;
    });
    setHydrated(true);
  }, [bootstrap.status, bootstrap.data]);

  const signIn = useCallback(async (input: SignInInput) => {
    setMutating(true);
    try {
      setSession(await apiClient.signIn(input));
    } finally {
      setMutating(false);
    }
  }, [apiClient]);

  const signInWithGoogle = useCallback(async (input: GoogleLoginInput) => {
    setMutating(true);
    try {
      const result = await apiClient.signInWithGoogle(input);
      setSession(result.session);
      return result.hasCompletedOnboarding;
    } finally {
      setMutating(false);
    }
  }, [apiClient]);

  const signUp = useCallback(async (input: SignUpInput) => {
    setMutating(true);
    try {
      setSession(await apiClient.signUp(input));
    } finally {
      setMutating(false);
    }
  }, [apiClient]);

  const signOut = useCallback(async () => {
    setMutating(true);
    try {
      await apiClient.signOut();
      setSession(null);
    } finally {
      setMutating(false);
    }
  }, [apiClient]);

  const refreshSession = useCallback(async () => {
    setMutating(true);
    try {
      const nextSession = (await apiClient.getBootstrap()).session;
      setSession(nextSession);
      return nextSession;
    } finally {
      setMutating(false);
    }
  }, [apiClient]);

  const value = useMemo(
    () => ({
      session,
      loading: bootstrap.status === "loading" || !hydrated || mutating,
      signIn,
      signInWithGoogle,
      signUp,
      refreshSession,
      signOut,
    }),
    [
      session,
      bootstrap.status,
      hydrated,
      mutating,
      signIn,
      signInWithGoogle,
      signUp,
      refreshSession,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
}
